import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  useCommissionOffers, 
  useArtistCommissionOffers,
  useClientCommissionRequests 
} from "@/hooks/commission";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUser } from "@/hooks/use-user";
import { PlusCircle, AlertCircle, FileText } from "lucide-react";
import { CommissionCard } from "@/components/commissions/CommissionCard";
import { CommissionRequestCard } from "@/components/commissions/CommissionRequestCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { CommissionOffer, CommissionRequest } from "@/types/commission.types";
import { useIsMobile } from "@/hooks/use-mobile";

interface CommissionsGridProps {
  searchFilter?: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

export function CommissionsGrid({ searchFilter = "", activeTab, setActiveTab }: CommissionsGridProps) {
  const { user } = useUser();
  const isMobile = useIsMobile();
  
  // Use the optimized hooks with pagination
  const { 
    offers: allCommissions, 
    isLoading: isLoadingAll,
    isError: isErrorAll,
    error: errorAll,
    page: explorePage, 
    setPage: setExplorePage, 
    totalCount: allCount 
  } = useCommissionOffers();
  
  const { 
    offers: myCommissions, 
    isLoading: isLoadingMine,
    isError: isErrorMine,
    error: errorMine,
    page: minePage,
    setPage: setMinePage,
    totalCount: myCount
  } = useArtistCommissionOffers(user?.id || "");
  
  // Novo hook para as comissões contratadas
  const {
    requests: myRequests,
    isLoading: isLoadingRequests,
    isError: isErrorRequests,
    error: errorRequests,
    page: requestsPage,
    setPage: setRequestsPage,
    totalCount: requestsCount
  } = useClientCommissionRequests(user?.id || "");

  const isLoading = activeTab === "explore" 
    ? isLoadingAll 
    : activeTab === "mine" 
      ? isLoadingMine 
      : isLoadingRequests;
  
  const isError = activeTab === "explore" 
    ? isErrorAll 
    : activeTab === "mine" 
      ? isErrorMine 
      : isErrorRequests;
  
  const error = activeTab === "explore" 
    ? errorAll 
    : activeTab === "mine" 
      ? errorMine 
      : errorRequests;
  
  const commissions = activeTab === "explore" ? allCommissions : myCommissions;
  const currentPage = activeTab === "explore" 
    ? explorePage 
    : activeTab === "mine" 
      ? minePage 
      : requestsPage;
  
  const setCurrentPage = activeTab === "explore" 
    ? setExplorePage 
    : activeTab === "mine" 
      ? setMinePage 
      : setRequestsPage;
  
  const totalCount = activeTab === "explore" 
    ? allCount 
    : activeTab === "mine" 
      ? myCount 
      : requestsCount;
  
  // Calculate total pages
  const ITEMS_PER_PAGE = 6;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Filter commissions based on search query
  const filterCommissions = (commissions: CommissionOffer[]) => {
    if (!searchFilter || searchFilter.trim() === "") return commissions;
    
    const lowerSearchFilter = searchFilter.toLowerCase().trim();
    
    return commissions.filter(commission => {
      // Search in title
      if (commission.title && commission.title.toLowerCase().includes(lowerSearchFilter)) {
        return true;
      }
      
      // Search in description
      if (commission.description_comission && 
          commission.description_comission.toLowerCase().includes(lowerSearchFilter)) {
        return true;
      }
      
      // Search in tags
      if (commission.tags && commission.tags.some(tag => 
        tag.toLowerCase().includes(lowerSearchFilter))) {
        return true;
      }
      
      // Search in specific commissions
      if (commission.specific_commissions && commission.specific_commissions.some(type => 
        type.toLowerCase().includes(lowerSearchFilter))) {
        return true;
      }
      
      // Search by price range (e.g. "under 50")
      if (lowerSearchFilter.includes("under") || lowerSearchFilter.includes("menos") || 
          lowerSearchFilter.includes("abaixo")) {
        const priceMatch = lowerSearchFilter.match(/\d+/);
        if (priceMatch && commission.price_usd < parseInt(priceMatch[0])) {
          return true;
        }
      }
      
      // Search by exact price
      if (!isNaN(parseFloat(lowerSearchFilter))) {
        const searchPrice = parseFloat(lowerSearchFilter);
        if (Math.abs(commission.price_usd - searchPrice) < 0.01) {
          return true;
        }
      }
      
      return false;
    });
  };
  
  // Filter commission requests based on search query
  const filterRequests = (requests: CommissionRequest[]) => {
    if (!searchFilter || searchFilter.trim() === "") return requests;
    
    const lowerSearchFilter = searchFilter.toLowerCase().trim();
    
    return requests.filter(request => {
      // Search in description
      if (request.description && request.description.toLowerCase().includes(lowerSearchFilter)) {
        return true;
      }
      
      // Search by status
      if (request.status && request.status.toLowerCase().includes(lowerSearchFilter)) {
        return true;
      }
      
      // Search by price
      if (!isNaN(parseFloat(lowerSearchFilter))) {
        const searchPrice = parseFloat(lowerSearchFilter);
        if (Math.abs(request.final_price_usd - searchPrice) < 0.01 || 
            Math.abs(request.final_price_eth - searchPrice) < 0.01) {
          return true;
        }
      }
      
      return false;
    });
  };
  
  const filteredCommissions = filterCommissions(commissions);
  const filteredRequests = filterRequests(myRequests);

  // Display error if there's a problem loading the data
  if (isError) {
    console.error('Error loading commissions:', error);
    
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Comissões</h1>
          <Link to="/commissions/create">
            <Button className={isMobile ? "w-full" : ""}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Criar Comissão
            </Button>
          </Link>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/10 p-8 rounded-lg flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">Erro ao carregar comissões</h3>
          <p className="text-muted-foreground mb-6">Ocorreu um erro ao buscar as comissões. Por favor, tente novamente mais tarde.</p>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Comissões</h1>
        <Link to="/commissions/create">
          <Button className={isMobile ? "w-full" : ""}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Criar Comissão
          </Button>
        </Link>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`mb-6 ${isMobile ? 'w-full mobile-tabs' : ''}`}>
          <TabsTrigger value="explore" className={isMobile ? "flex-1" : ""}>Explorar</TabsTrigger>
          {user && (
            <>
              <TabsTrigger value="mine" className={isMobile ? "flex-1" : ""}>Minhas Comissões</TabsTrigger>
              <TabsTrigger value="hired" className={isMobile ? "flex-1" : ""}>Contratadas</TabsTrigger>
            </>
          )}
        </TabsList>
        
        <TabsContent value="explore" className={isMobile ? "mobile-tabs-content" : ""}>
          <div className="bg-gray-50 dark:bg-gray-900/30 p-6 rounded-lg">
            {isLoadingAll ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((idx) => (
                  <Card key={idx} className="overflow-hidden">
                    <Skeleton className="w-full h-48" />
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div>
                          <Skeleton className="h-5 w-24 mb-1" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                      <Skeleton className="h-6 w-full mb-2" />
                      <Skeleton className="h-5 w-16 mb-2" />
                      <Skeleton className="h-4 w-32 mb-3" />
                      <Skeleton className="h-9 w-full mt-2" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredCommissions.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCommissions.map((commission) => (
                    <CommissionCard key={commission.id} offer={commission} />
                  ))}
                </div>
                
                {searchFilter === "" && totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                {searchFilter ? (
                  <p className="text-muted-foreground">Nenhuma comissão encontrada para "{searchFilter}".</p>
                ) : (
                  <p className="text-muted-foreground">Nenhuma comissão disponível no momento.</p>
                )}
                
                {user && (
                  <Button className="mt-4" asChild>
                    <Link to="/commissions/create">
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Criar Comissão
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>
        
        {user && (
          <>
            <TabsContent value="mine" className={isMobile ? "mobile-tabs-content" : ""}>
              {isLoadingMine ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((idx) => (
                    <Card key={idx} className="overflow-hidden">
                      <Skeleton className="w-full h-48" />
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <Skeleton className="h-10 w-10 rounded-full" />
                          <div>
                            <Skeleton className="h-5 w-24 mb-1" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                        </div>
                        <Skeleton className="h-6 w-full mb-2" />
                        <Skeleton className="h-5 w-16 mb-2" />
                        <Skeleton className="h-4 w-32 mb-3" />
                        <Skeleton className="h-9 w-full mt-2" />
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filterCommissions(myCommissions).map((commission) => (
                      <CommissionCard key={commission.id} offer={commission} />
                    ))}
                    
                    {myCommissions.length === 0 && (
                      <div className="text-center py-12 col-span-full">
                        <h3 className="text-lg font-medium mb-2">Você ainda não tem comissões</h3>
                        <p className="text-muted-foreground mb-4">Crie sua primeira oferta de comissão para começar</p>
                        <Link to="/commissions/create">
                          <Button>
                            <PlusCircle className="h-4 w-4 mr-2" />
                            Criar Comissão
                          </Button>
                        </Link>
                      </div>
                    )}
                    
                    {myCommissions.length > 0 && searchFilter !== "" && filterCommissions(myCommissions).length === 0 && (
                      <div className="text-center py-12 col-span-full">
                        <p className="text-muted-foreground">Nenhuma de suas comissões corresponde à pesquisa "{searchFilter}".</p>
                        <Button 
                          variant="link" 
                          onClick={() => window.location.href = "/commissions"}
                          className="mt-2"
                        >
                          Limpar filtro
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {searchFilter === "" && myCommissions.length > 0 && totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              )}
            </TabsContent>
            
            <TabsContent value="hired" className={isMobile ? "mobile-tabs-content" : ""}>
              <div className="bg-gray-50 dark:bg-gray-900/30 p-6 rounded-lg">
                {isLoadingRequests ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((idx) => (
                      <Card key={idx} className="overflow-hidden">
                        <Skeleton className="w-full h-48" />
                        <div className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div>
                              <Skeleton className="h-5 w-24 mb-1" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                          <Skeleton className="h-6 w-full mb-2" />
                          <Skeleton className="h-5 w-16 mb-2" />
                          <Skeleton className="h-4 w-32 mb-3" />
                          <Skeleton className="h-9 w-full mt-2" />
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : filteredRequests.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredRequests.map((request) => (
                        <CommissionRequestCard key={request.id} request={request} />
                      ))}
                    </div>
                    
                    {searchFilter === "" && totalPages > 1 && (
                      <div className="mt-8 flex justify-center">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    {searchFilter ? (
                      <p className="text-muted-foreground">Nenhuma comissão contratada encontrada para "{searchFilter}".</p>
                    ) : (
                      <div className="flex flex-col items-center justify-center">
                        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">Você ainda não contratou comissões</h3>
                        <p className="text-muted-foreground mb-4">Explore as comissões disponíveis e contrate um artista.</p>
                        <Button 
                          variant="outline" 
                          onClick={() => setActiveTab("explore")}
                        >
                          Explorar comissões
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
